import {useDraggable} from "@dnd-kit/core";
import clsx from "clsx";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import SQLTableFilterComponent from "../../sql-chart/SQLTableFilterComponent";
import {
    GROUP_SORT_ICONS,
    GROUP_SORT_LABELS,
    nextGroupSort,
    normalizeGroupSort,
} from "./asyncTableGroupRules";
import useDragEvents from "../../../hooks/useDragEvents";

const MIN_COLUMN_WIDTH = 100;

function ResizeHandler({container, index, minWidth = MIN_COLUMN_WIDTH}) {
    const drag = useRef();

    const onDrag = useCallback(
        (e) => {
            const rect = container.current.getBoundingClientRect();
            const clientX = e.type.startsWith("touch")
                ? e.touches[0].clientX
                : e.clientX;

            let width = clientX - rect.left;
            if (width < minWidth) width = minWidth;

            const table = container.current.closest("table");

            const cols = table.querySelectorAll("colgroup col");
            cols[index].style.width = width + "px";

            const totalWidth = Array.from(cols).reduce((sum, col) => {
                const w = parseFloat(col.style.width) || col.offsetWidth || 0;
                return sum + w;
            }, 0);
            table.style.width = `${totalWidth}px`;
        },
        [container, index, minWidth]
    );

    useDragEvents(drag, onDrag);

    return <div className="resize-handler" ref={drag} />;
}

function IndexHeader({rowSpan, colIndex = 0}) {
    const container = useRef();

    const [width, setWidth] = useState(null);

    useEffect(() => {
        const rect = container.current.getBoundingClientRect();
        setWidth(rect.width);
    }, []);

    return (
        <th
            ref={container}
            style={{width}}
            rowSpan={rowSpan}
            data-children={false}>
            <div>#</div>
            <ResizeHandler container={container} index={colIndex} />
        </th>
    );
}

function HeaderItem({
    header,
    setSort,
    sort,
    setFilters,
    filters,
    id,
    disabled,
    groupSorts,
    onGroupSortChange,
}) {
    const {t} = useTranslation();

    const container = useRef();

    const [showFilter, setShowFilter] = useState(false);

    const filterField = header.filterColumn || header.field;

    const {sortStatus, index} = useMemo(() => {
        const index = sort.findIndex((item) => item.field === filterField);
        if (index === -1) return {sortStatus: null, index};
        return {sortStatus: sort[index].direction, index};
    }, [sort, filterField]);

    // kolumna poziomu "Każda wartość" sortuje grupy (w grupie wszystkie wiersze mają tę samą wartość);
    // poziom z regułą i zwykła kolumna sortują wiersze
    const sortsGroups =
        header.groupLevel !== undefined &&
        !header.groupRuleLevel &&
        !!onGroupSortChange;
    // bez zapisanej kolejności grupy i tak są posortowane (rosnąco) - ikona zawsze widoczna
    const groupSort = sortsGroups
        ? normalizeGroupSort(groupSorts?.[header.columnKey])
        : undefined;

    const onClick = useCallback(() => {
        if (sortsGroups) {
            onGroupSortChange(header.columnKey, nextGroupSort(groupSort));
            return;
        }
        setSort((prev) => {
            const copy = prev.slice();
            const index = copy.findIndex((item) => item.field === filterField);
            if (index === -1) {
                copy.push({
                    field: filterField,
                    direction: "ASC",
                    customSort: header.customSort,
                });
            } else {
                const item = copy[index];
                if (item.direction === "ASC") {
                    copy[index] = {
                        field: filterField,
                        direction: "DESC",
                        customSort: header.customSort,
                    };
                } else {
                    copy.splice(index, 1);
                }
            }
            return copy;
        });
    }, [
        sortsGroups,
        onGroupSortChange,
        header.columnKey,
        groupSort,
        setSort,
        filterField,
        header.customSort,
    ]);

    const onShowFilterClick = useCallback(() => {
        setShowFilter(true);
    }, []);

    const hasChildren = !!header.children && header.children.length > 0;

    // nazwe kolumny mozna przeciagnac na strefe "Grupuj wedlug" nad tabela
    const {
        setNodeRef: setDragRef,
        listeners,
        isDragging,
    } = useDraggable({
        id: header.columnKey ?? `header-${header.colIndex}`,
        data: {column: header},
        disabled: hasChildren || disabled || !header.columnKey,
    });

    const className = useMemo(() => {
        let className = "";
        if (hasChildren) className += "with-children";
        if (disabled) className += " disabled";
        if (isDragging) className += " dragging";
        return className;
    }, [hasChildren, disabled, isDragging]);

    useEffect(() => {
        setShowFilter(false);
    }, [disabled]);

    return (
        <th
            ref={container}
            rowSpan={header.rowspan}
            colSpan={header.colspan}
            data-children={hasChildren}
            className={className}>
            <div>
                <div
                    ref={setDragRef}
                    {...listeners}
                    className={clsx({
                        "group-draggable": !hasChildren,
                    })}
                    onClick={!hasChildren && !disabled ? onClick : null}>
                    {header.name}
                </div>
                {!hasChildren && (
                    <ResizeHandler
                        container={container}
                        index={header.colIndex}
                    />
                )}
                {!hasChildren &&
                    (sortStatus || groupSort || header.filterType) && (
                        <div>
                            {header.filterType && (
                                <div
                                    onClick={
                                        !disabled ? onShowFilterClick : null
                                    }>
                                    <i
                                        className={`fa-solid fa-filter${filters[filterField] ? " success" : ""}`}
                                    />
                                </div>
                            )}
                            {groupSort && (
                                <div title={t(GROUP_SORT_LABELS[groupSort])}>
                                    <i
                                        className={`fa-solid ${GROUP_SORT_ICONS[groupSort]}`}
                                    />
                                </div>
                            )}
                            {sortStatus && (
                                <div>
                                    <i
                                        className={`fa-solid fa-arrow-${sortStatus === "ASC" ? "up" : "down"}-wide-short`}
                                    />
                                    <span>({index + 1})</span>
                                </div>
                            )}
                        </div>
                    )}
            </div>
            <SQLTableFilterComponent
                filters={filters}
                setFilters={setFilters}
                setShowFilter={setShowFilter}
                show={showFilter}
                header={header}
                parent={container}
                ResultID={id}
            />
        </th>
    );
}

export default function AsyncTableHeaders({
    headers,
    shouldIndex,
    setSort,
    sort,
    filters,
    setFilters,
    id,
    disabled,
    indexPosition = 0,
    groupSorts,
    onGroupSortChange,
}) {
    const processedHeaders = useMemo(() => {
        const calculateDepth = (item) => {
            if (!item.children || item.children.length === 0) return 1;
            return 1 + Math.max(...item.children.map(calculateDepth));
        };

        const maxDepth = Math.max(...headers.map(calculateDepth));

        const getColspan = (header) => {
            if (!header.children || header.children.length === 0) {
                return 1;
            }
            return header.children.reduce(
                (sum, child) => sum + getColspan(child),
                0
            );
        };

        const getRowspan = (header, currentDepth) => {
            if (!header.children || header.children.length === 0) {
                return maxDepth - currentDepth + 1;
            }
            return 1;
        };

        const rows = Array.from({length: maxDepth}, () => []);
        let colIndex = 0;

        const processHeaders = (items, depth = 0) => {
            items.forEach((item, position) => {
                // miejsce na kolumnę indeksu w colgroup (przed `indexPosition`-tym nagłówkiem najwyższego poziomu)
                if (shouldIndex && depth === 0 && position === indexPosition) {
                    colIndex++;
                }
                const colspan = getColspan(item);
                const rowspan = getRowspan(item, depth + 1);

                let index = null;
                if (!item.children || item.children.length === 0) {
                    index = colIndex;
                    colIndex++;
                }

                rows[depth].push({
                    ...item,
                    colspan,
                    rowspan,
                    colIndex: index,
                });

                if (item.children && item.children.length > 0) {
                    processHeaders(item.children, depth + 1);
                }
            });
        };

        processHeaders(headers);
        return rows;
    }, [headers, shouldIndex, indexPosition]);

    return (
        <thead>
            {processedHeaders.map((row, index) => (
                <tr key={index}>
                    {row.map((header, headerIndex) => (
                        <React.Fragment key={headerIndex}>
                            {shouldIndex &&
                                index === 0 &&
                                headerIndex === indexPosition && (
                                    <IndexHeader
                                        rowSpan={processedHeaders.length}
                                        colIndex={indexPosition}
                                    />
                                )}
                            <HeaderItem
                                header={header}
                                setSort={setSort}
                                sort={sort}
                                filters={filters}
                                setFilters={setFilters}
                                id={id}
                                disabled={disabled}
                                groupSorts={groupSorts}
                                onGroupSortChange={onGroupSortChange}
                            />
                        </React.Fragment>
                    ))}
                </tr>
            ))}
        </thead>
    );
}
